import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { AddWidgetBelowIcon } from '@pleio/icons/add-widget-below'
import { CopyIcon } from '@pleio/icons/copy'
import { CrossIcon } from '@pleio/icons/cross'
import { DeleteIcon } from '@pleio/icons/delete'
import { EditIcon } from '@pleio/icons/edit'
import { MoveIcon } from '@pleio/icons/move'

import Popover from 'js/components/Popover'
import ActionsBarButton from 'js/page/PageEditor/components/ActionsBarButton'
import ThemeProvider from 'js/theme/ThemeProvider'

import isEditableWidget from '../helpers/isEditableWidget'

import ActionsBar from './ActionsBar'
import WidgetPicker from './WidgetPicker'

const WidgetActions = ({
    show,
    id,
    entity,
    snapshot,
    provided,
    widgetEditing,
    showWidgetPicker,
    setShowWidgetPicker,
    onEdit,
    onDelete,
    onAdd,
    onDuplicate,
    onFocus,
    onBlur,
}) => {
    const { t } = useTranslation()

    const popoverActionsRef = useRef(null)

    const canEdit = isEditableWidget(entity?.type)

    const { widgetGuid } = entity

    const hideWidgetPicker = () => {
        const popoverActions = popoverActionsRef?.current
        if (popoverActions) {
            popoverActions.close()
        }
    }

    const addWidget = (type) => {
        hideWidgetPicker()
        onAdd?.(type)
    }

    const actionsBarProps = {
        isWidget: true,
        show,
        onFocus,
        onBlur,
    }

    return (
        <ThemeProvider theme="pleio" className="WidgetIgnoreGrow">
            <ActionsBar {...actionsBarProps}>
                {!snapshot.isDragging && canEdit && (
                    <ActionsBarButton
                        variant="primary"
                        id={`${id}-edit-widget`}
                        onClick={onEdit}
                        tooltip={t('page.edit-widget')}
                    >
                        <EditIcon />
                    </ActionsBarButton>
                )}
                <ActionsBarButton
                    {...provided.dragHandleProps}
                    as="div"
                    variant={widgetEditing ? 'tertiary' : 'primary'}
                    disabled={!!widgetEditing}
                    aria-describedby={`aria-widget-${widgetGuid}`}
                    tooltip={t('page.move-widget')}
                >
                    <MoveIcon />
                </ActionsBarButton>
                {!snapshot.isDragging && (
                    <ActionsBarButton
                        variant="primary"
                        onClick={onDelete}
                        tooltip={t('page.delete-widget')}
                        data-testid="button-delete-widget"
                    >
                        <DeleteIcon />
                    </ActionsBarButton>
                )}
            </ActionsBar>
            {!snapshot.isDragging && (
                <ActionsBar {...actionsBarProps} position="bottom">
                    <Popover
                        actionsRef={popoverActionsRef}
                        open={showWidgetPicker}
                        setOpen={setShowWidgetPicker}
                        content={<WidgetPicker onAddWidget={addWidget} />}
                        side="top"
                        sideOffset={1}
                    >
                        <ActionsBarButton
                            id={`${id}-add-widget`}
                            variant={showWidgetPicker ? 'secondary' : 'primary'}
                            tooltip={
                                showWidgetPicker
                                    ? t('page.close-widgets')
                                    : t('page.add-widget')
                            }
                            tooltipProps={{
                                theme: !showWidgetPicker
                                    ? 'primary'
                                    : 'default',
                                side: 'left',
                            }}
                        >
                            {showWidgetPicker ? (
                                <CrossIcon />
                            ) : (
                                <AddWidgetBelowIcon />
                            )}
                        </ActionsBarButton>
                    </Popover>
                    <ActionsBarButton
                        variant="primary"
                        id={`${id}-duplicate-widget`}
                        onClick={onDuplicate}
                        tooltip={t('page.duplicate-widget')}
                        tooltipProps={{ side: 'right' }}
                        data-testid="button-duplicate-widget"
                    >
                        <CopyIcon />
                    </ActionsBarButton>
                </ActionsBar>
            )}
        </ThemeProvider>
    )
}

export default WidgetActions
